import { useEffect, useState } from "react";
import { Box, Button, TextField, Grid, MenuItem } from "@mui/material";
import {
  PrimaryTypography,
  StyledTypography,
  StyledSecondaryTypography,
  ErrorTypography,
} from "../../Theme";
import { ReactComponent as BackArrow } from "../../Icons/BorderBackArrow.svg";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../Redux/AxiosInstance";
import { PostLoaderModal } from "../../CommonComponents/modal";
import { showToast } from "../../CommonComponents/Toaster";

const AddService = () => {
  const { propertyId } = useSelector((state) => state.PropertyDetails);
  const { LanguageData } = useSelector((state) => state.Language);

  const [formDatas, setFormDatas] = useState({
    name: "",
    unit: "",
    price: "",
    qty: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [postLoader, setPostLoader] = useState(false);
  const [dropDownData, setDropDownData] = useState([]);
  const navigate = useNavigate();

  const fetchDropDownDatas = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}getDropDownDatas?type=item_units`
      );
      if (response?.data?.success) {
        setDropDownData(response.data?.data?.drop_down || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDropDownDatas();
  }, []);

  const validateForm = () => {
    let errors = {};

    if (!formDatas.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formDatas.unit) {
      errors.unit = "Unit is required";
    }

    if (!formDatas.qty) {
      errors.qty = "Quantity is required";
    } else if (!/^[0-9]+$/.test(formDatas.qty)) {
      errors.qty = "Quantity must be a number";
    }

    if (!formDatas.price) {
      errors.price = "Price is required";
    } else if (!/^[0-9]+(\.[0-9]{1,2})?$/.test(formDatas.price)) {
      errors.price = "Price must be a valid number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setPostLoader(true);
      const res = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}item-master/${propertyId}`,
        {
          item_name: formDatas.name,
          unit_id: formDatas.unit,
          price: formDatas.price,
          quantity: formDatas.qty,
        }
      );

      if (res.data?.success) {
        showToast(res.data?.message);
        navigate("/item-list");
      }
    } catch (error) {
      showToast("Something went wrong. Please try again later", "error");
    } finally {
      setPostLoader(false);
    }
  };

  return (
    <>
      {postLoader && <PostLoaderModal />}
      <Box sx={{ padding: "0 20px", maxWidth: "728px", margin: "0 auto" }}>
        <Box>
          <BackArrow
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/item-list")}
          />
          <PrimaryTypography sx={{ marginTop: "10px" }}>
            Add Item
          </PrimaryTypography>
          <StyledSecondaryTypography>
            Add a new item to the invoice list.
          </StyledSecondaryTypography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "6px",
            marginTop: "20px",
          }}
        >
          <Box sx={{ padding: "15px 20px 15px 20px" }}>
            <StyledTypography> Product Name</StyledTypography>
            <TextField
              name="name"
              inputProps={{ maxLength: 20 }}
              InputProps={{ disableUnderline: true }}
              fullWidth
              placeholder="Enter name"
              sx={{
                backgroundColor: formErrors.name ? "#FEE4E2" : "#F9FAFB",
              }}
              value={formDatas.name}
              onChange={(e) => {
                setFormDatas({ ...formDatas, name: e.target.value });
                setFormErrors((prev) => ({ ...prev, name: "" }));
              }}
              error={!!formErrors.name}
            />
            <ErrorTypography>{formErrors.name}</ErrorTypography>
          </Box>

          <Grid
            container
            xs={12}
            gap="2.8%"
            sx={{ backgroundColor: "white", padding: "15px 20px 15px 20px" }}
          >
            <Grid xs={12} sm={5.8}>
              <StyledTypography>Unit</StyledTypography>
              <TextField
                select
                name="unit"
                fullWidth
                value={formDatas.unit}
                onChange={(e) => {
                  setFormDatas({ ...formDatas, unit: e.target.value });
                  setFormErrors((prev) => ({ ...prev, unit: "" }));
                }}
                InputProps={{ disableUnderline: true }}
                sx={{
                  backgroundColor: formErrors.unit ? "#FEE4E2" : "#F9FAFB",
                }}
                error={!!formErrors.unit}
              >
                <MenuItem disabled value="">
                  <em>Select unit</em>
                </MenuItem>
                {dropDownData.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option?.title}
                  </MenuItem>
                ))}
              </TextField>
              <ErrorTypography>{formErrors.unit}</ErrorTypography>
            </Grid>
            <Grid xs={12} sm={5.8}>
              <StyledTypography>Qty</StyledTypography>
              <TextField
                name="qty"
                inputProps={{ maxLength: 20 }}
                InputProps={{ disableUnderline: true }}
                fullWidth
                placeholder="Enter quantity"
                sx={{
                  backgroundColor: formErrors.qty ? "#FEE4E2" : "#F9FAFB",
                }}
                value={formDatas.qty}
                onChange={(e) => {
                  setFormDatas({ ...formDatas, qty: e.target.value });
                  setFormErrors((prev) => ({ ...prev, qty: "" }));
                }}
                error={!!formErrors.qty}
              />
              <ErrorTypography>{formErrors.qty}</ErrorTypography>
            </Grid>
            <Grid xs={12} sm={12} mt={4}>
              <StyledTypography>Price</StyledTypography>
              <TextField
                name="price"
                inputProps={{ maxLength: 20 }}
                InputProps={{ disableUnderline: true }}
                fullWidth
                placeholder="Enter price"
                sx={{
                  backgroundColor: formErrors.price ? "#FEE4E2" : "#F9FAFB",
                }}
                value={formDatas.price}
                onChange={(e) => {
                  setFormDatas({ ...formDatas, price: e.target.value });
                  setFormErrors((prev) => ({ ...prev, price: "" }));
                }}
                error={!!formErrors.price}
              />
              <ErrorTypography>{formErrors.price}</ErrorTypography>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              marginTop: "10px",
            }}
          >
            <Button
              sx={{
                marginTop: "20px",
                marginBottom: "20px",
                marginRight: "20px",
                padding: "20px",
                backgroundColor: "#E7EFF3",
                color: "#0E5E84",
                "&:hover": {
                  backgroundColor: "#CFDFE6",
                },
              }}
              onClick={handleSubmit}
              disabled={postLoader}
            >
              {LanguageData?.Submit || "Submit"}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AddService;
